import { useRouter } from "@tanstack/react-router";
import { posthog } from "lib/analytics";
import { useUserContext } from "lib/state/UserContextProvider";
import { useEffect, useMemo } from "react";

/**
 * Mirrors the current user's resolved feature/product state out of React into
 * two global sinks:
 *
 * 1. The TanStack Router context, so `beforeLoad` callbacks on legacy and
 *    public routes can read flags / products synchronously — primarily used
 *    by the showroom unification redirects.
 * 2. PostHog super properties, so every event (custom, `$pageview`,
 *    autocapture) carries the flag set that was live when it fired.
 *
 * Rendered inside both branches of `AppContainer` so it sees the right
 * features on both authed pages (via `UserContextProvider`) and public
 * pages (via the public-page fallback in `useUserContext`).
 *
 * After pushing to the router, `router.invalidate()` re-runs `beforeLoad` so a
 * direct hit on a legacy URL with the flag on bounces as soon as the user /
 * builder has hydrated. The analytics push is kept in a separate effect so it
 * can never trigger that invalidation.
 */
export default function UserContextSync() {
  const router = useRouter();
  const { enabledFeatures, enabledProducts, featuresLoaded, userLoading, builderLoading } = useUserContext();
  // Don't mirror into router context until the underlying data has loaded.
  const hydrated = !userLoading && !builderLoading;

  useEffect(() => {
    if (!hydrated) {
      return;
    }
    router.update({ context: { enabledFeatures, enabledProducts, flagsHydrated: true } });
    void router.invalidate();
  }, [router, enabledFeatures, enabledProducts, hydrated]);

  // One array property rather than a key per flag: super properties merge, so a
  // per-flag `true` would go stale the moment a flag was switched off.
  const analyticsFeatureFlags = useMemo(
    () =>
      Object.entries(enabledFeatures)
        .filter(([, enabled]) => enabled)
        .map(([featureName]) => featureName)
        .sort(),
    [enabledFeatures],
  );

  useEffect(() => {
    posthog.register({
      enabled_feature_flags: analyticsFeatureFlags,
      feature_flags_loaded: featuresLoaded,
    });
  }, [analyticsFeatureFlags, featuresLoaded]);

  return null;
}
